import { webCoreTypes } from "@core/core-types.di";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { IotApiClient } from "@iot-platform/core";
import type {
    NotebookMessageDto,
    NotebookMessagesDto,
} from "@notebook/data/dto/notebook-message.dto";
import { NotebookMessageMapper } from "@notebook/data/mappers/notebook-message.mapper";
import type { NotebookMessageCreateError } from "@notebook/domain/errors/notebook-message-create.error";
import type { NotebookMessageFileCreateError } from "@notebook/domain/errors/notebook-message-file-create.error";
import type { NotebookMessageUpdateError } from "@notebook/domain/errors/notebook-message-update.error";
import type {
    NotebookMessage,
    NotebookMessageCreate,
    NotebookMessageFileCreate,
    NotebookMessages,
    NotebookMessageUpdate,
} from "@notebook/domain/models/notebook-message.model";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";

@injectable()
export class NotebookMessageDatasource {
    static readonly NOTEBOOK_MESSAGE_PATH =
        "/notebooks/:notebookId/notebook_messages";
    static readonly NOTEBOOK_MESSAGE_FILES_PATH =
        "/notebooks/:notebookId/notebook_messages/:notebookMessageId/files";

    constructor(
        @inject(webCoreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(NotebookMessageMapper)
        private readonly notebookMessageMapper: NotebookMessageMapper,
    ) {}

    fetchAll(notebookId: string): ResultAsync<NotebookMessages, FallbackError> {
        return this.iotApiClient.http
            .get<NotebookMessagesDto>(
                NotebookMessageDatasource.NOTEBOOK_MESSAGE_PATH.replace(
                    ":notebookId",
                    notebookId,
                ),
            )
            .mapErr(() => new FallbackError())
            .map((response) =>
                this.notebookMessageMapper.mapList(response.data),
            );
    }

    fetchById(
        notebookMessageId: string,
        notebookId: string,
    ): ResultAsync<Nullable<NotebookMessage>, FallbackError> {
        return this.iotApiClient.http
            .get<NotebookMessageDto>(
                `${NotebookMessageDatasource.NOTEBOOK_MESSAGE_PATH.replace(":notebookId", notebookId)}/${notebookMessageId}/`,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.notebookMessageMapper
                    .map(response.data)
                    .mapErr(() => new FallbackError()),
            );
    }

    create(
        notebookId: string,
        createNotebookMessage: NotebookMessageCreate,
    ): ResultAsync<NotebookMessage, NotebookMessageCreateError> {
        const formData = this.notebookMessageMapper.mapToCreateBody(
            createNotebookMessage,
        );

        return this.iotApiClient.http
            .post<NotebookMessageDto>(
                NotebookMessageDatasource.NOTEBOOK_MESSAGE_PATH.replace(
                    ":notebookId",
                    notebookId,
                ),
                formData,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.notebookMessageMapper.map(response.data),
            );
    }

    update(
        notebookMessageId: string,
        notebookId: string,
        updateNotebookMessage: NotebookMessageUpdate,
    ): ResultAsync<NotebookMessage, NotebookMessageUpdateError> {
        const formData = this.notebookMessageMapper.mapToEditBody(
            updateNotebookMessage,
        );

        return this.iotApiClient.http
            .patch<NotebookMessageDto>(
                `${NotebookMessageDatasource.NOTEBOOK_MESSAGE_PATH.replace(":notebookId", notebookId)}/${notebookMessageId}`,
                formData,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.notebookMessageMapper.map(response.data),
            );
    }

    delete(
        notebookId: string,
        notebookMessageId: string,
    ): ResultAsync<true, FallbackError> {
        return this.iotApiClient.http
            .delete(
                `${NotebookMessageDatasource.NOTEBOOK_MESSAGE_PATH.replace(":notebookId", notebookId)}/${notebookMessageId}`,
            )
            .mapErr(() => new FallbackError())
            .map(() => true);
    }

    createFile(
        notebookId: string,
        notebookMessageId: string,
        createNotebookMessageFile: NotebookMessageFileCreate,
    ): ResultAsync<boolean, NotebookMessageFileCreateError> {
        const formData = this.notebookMessageMapper.mapToCreateFileBody(
            createNotebookMessageFile,
        );

        return this.iotApiClient.http
            .post<NotebookMessageDto>(
                NotebookMessageDatasource.NOTEBOOK_MESSAGE_FILES_PATH.replace(
                    ":notebookId",
                    notebookId,
                ).replace(":notebookMessageId", notebookMessageId),
                formData,
            )
            .mapErr(() => new FallbackError())
            .map(() => true);
    }

    deleteFile(
        notebookId: string,
        notebookMessageId: string,
        fileId: string,
    ): ResultAsync<true, FallbackError> {
        return this.iotApiClient.http
            .delete(
                `${NotebookMessageDatasource.NOTEBOOK_MESSAGE_FILES_PATH.replace(
                    ":notebookId",
                    notebookId,
                ).replace(":notebookMessageId", notebookMessageId)}/${fileId}`,
            )
            .mapErr(() => new FallbackError())
            .map(() => true);
    }
}
